import { PlanCode } from '@/typings/domain'
import {
  BadgeJapaneseYen,
  BadgeMinus,
  CalendarClock,
  ClipboardType,
  Crown,
  EyeOff,
  FileDigit,
  FileJson2,
  FolderDown,
  Goal,
  HandHelping,
  Headset,
  Link2,
  MessageCircleMore,
  MessageSquareMore,
  Paperclip,
  ReceiptJapaneseYen,
  ServerCrash,
  ShieldCheck,
  Sparkles,
  TextSearch,
  ToolCase,
  UserPlus,
  Users,
} from 'lucide-react-native'
import { ComponentType } from 'react'

type Feature = {
  text: string
  icon: ComponentType<{ size?: number; color?: string }>
}

export type Plan = {
  code: PlanCode
  weight: number
  title: string
  description?: string
  pricePerYear: number
  features: Feature[]
  featuresQuota: {
    cooperatorLimit: number
    subAccount: number
  }
  monthlyResetQuota: {
    entries: number
  }
  isListedPublicly: boolean
  isOrg?: boolean
} & (
  | {
      supportMonthlyByDefault: true
      pricePerMonth: number
      perYearSavingTips: string
    }
  | {
      supportMonthlyByDefault: false
      pricePerMonth?: number
      perYearSavingTips?: string
    }
)

export const PLANS: Plan[] = [
  {
    code: PlanCode.Free,
    weight: 1,
    title: '免费版',
    pricePerYear: 0,
    isOrg: false,
    features: [],
    featuresQuota: {
      cooperatorLimit: 2,
      subAccount: 1,
    },
    monthlyResetQuota: {
      entries: 50000,
    },
    isListedPublicly: false,
    supportMonthlyByDefault: false,
  },
  {
    code: PlanCode.Pro2,
    weight: 1000,
    title: '专业版',
    description: '年付更省心',
    pricePerYear: 599,
    features: [
      { text: '1 人使用', icon: Users },
      { text: '1000 点/月 AI 点数额度', icon: Sparkles },
      { text: '1 GB/月 附件容量', icon: Paperclip },
      { text: '10 万/月 第三方收款限额', icon: BadgeJapaneseYen },
      { text: '无限额 小金商户收款', icon: ReceiptJapaneseYen },
      { text: '短信推送及企业签名', icon: MessageSquareMore },
      { text: '对外查询与数据分享', icon: TextSearch },
      { text: '考试成绩单', icon: FileDigit },
    ],
    featuresQuota: {
      cooperatorLimit: 5,
      subAccount: 1,
    },
    monthlyResetQuota: {
      entries: 100000,
    },
    isListedPublicly: true,
    supportMonthlyByDefault: false,
    pricePerMonth: 59,
  },
  {
    code: PlanCode.Ent2,
    weight: 1020,
    title: '专业增强版',
    pricePerYear: 2399,
    pricePerMonth: 299,
    features: [
      { text: '1 人使用', icon: Users },
      { text: '2000 点/月 AI 点数额度', icon: Sparkles },
      { text: '5 GB/月 附件容量', icon: Paperclip },
      { text: '20 万/月 第三方收款限额', icon: BadgeJapaneseYen },
      { text: '无限额 小金商户收款', icon: ReceiptJapaneseYen },
      { text: '附件一键打包下载', icon: FolderDown },
      { text: '高级字段：关联/NPS', icon: ClipboardType },
      { text: '专业营销组件', icon: ToolCase },
    ],
    featuresQuota: {
      cooperatorLimit: 10,
      subAccount: 1,
    },
    monthlyResetQuota: {
      entries: 100000,
    },
    isListedPublicly: true,
    supportMonthlyByDefault: true,
    perYearSavingTips: '省33%',
  },
  {
    code: PlanCode.E10,
    weight: 2010,
    title: '企业基础版',
    description: '年付更省心',
    pricePerMonth: 439,
    pricePerYear: 4399,
    isOrg: true,
    features: [
      { text: '5 人团队使用', icon: Users },
      { text: '3000 点/月 AI 点数额度', icon: Sparkles },
      { text: '10 GB/月 附件容量', icon: Paperclip },
      { text: '50 万/月 第三方收款限额', icon: BadgeJapaneseYen },
      { text: '无限额 小金商户收款', icon: ReceiptJapaneseYen },
      { text: '隐藏金数据 LOGO', icon: EyeOff },
      { text: '团队协作权限', icon: UserPlus },
      { text: '在线预约场景', icon: CalendarClock },
      { text: '专属客户服务', icon: Headset },
    ],
    featuresQuota: {
      cooperatorLimit: 10,
      subAccount: 5,
    },
    monthlyResetQuota: {
      entries: 100000,
    },
    isListedPublicly: true,
    supportMonthlyByDefault: false,
  },
  {
    code: PlanCode.E20,
    weight: 2020,
    title: '企业协作版',
    description: '年付更省心',
    pricePerMonth: 739,
    pricePerYear: 7399,
    isOrg: true,
    features: [
      { text: '10 人团队使用', icon: Users },
      { text: '4000 点/月 AI 点数额度', icon: Sparkles },
      { text: '20 GB/月 附件容量', icon: Paperclip },
      { text: '50 万/月 第三方收款限额', icon: BadgeJapaneseYen },
      { text: '无限额 小金商户收款', icon: ReceiptJapaneseYen },
      { text: '展示企业品牌 LOGO', icon: Crown },
      { text: '高级字段：签名/计算/表格', icon: ClipboardType },
      { text: '付费预约，灵活退改', icon: CalendarClock },
      { text: '自定义企业域名', icon: Link2 },
      { text: '专属客户服务', icon: Headset },
    ],
    featuresQuota: {
      cooperatorLimit: Number.POSITIVE_INFINITY,
      subAccount: 10,
    },
    monthlyResetQuota: {
      entries: 200000,
    },
    isListedPublicly: true,
    supportMonthlyByDefault: false,
  },
  {
    code: PlanCode.E30,
    weight: 2030,
    title: '企业高级版',
    description: '企业一站式业务数据平台',
    pricePerMonth: 2399,
    pricePerYear: 23999,
    isOrg: true,
    features: [
      { text: '50 人团队使用', icon: Users },
      { text: '第三方收款无限额', icon: BadgeJapaneseYen },
      { text: '第三方收款支持支付宝', icon: ReceiptJapaneseYen },
      { text: '去除表单页投诉入口', icon: BadgeMinus },
      { text: '企业级 API 和 URL 传参', icon: FileJson2 },
      { text: '企业培训服务 3 次', icon: HandHelping },
      { text: '常见技术服务支持', icon: MessageCircleMore },
      { text: '专属客户服务', icon: Headset },
    ],
    featuresQuota: {
      cooperatorLimit: Number.POSITIVE_INFINITY,
      subAccount: 50,
    },
    monthlyResetQuota: {
      entries: 500000,
    },
    isListedPublicly: true,
    supportMonthlyByDefault: false,
  },
  {
    code: PlanCode.EnCor,
    weight: 2040,
    title: '商业合作版',
    description: '大型企业或集团的数据平台',
    pricePerYear: 80000,
    isOrg: true,
    features: [
      { text: '100 人团队使用', icon: Users },
      { text: '全方位安全合规流程', icon: ShieldCheck },
      { text: '支持与现有系统集成', icon: ServerCrash },
      { text: '个性化业务解决方案', icon: Goal },
      { text: '1 对 1 专属客服全程服务', icon: Headset },
    ],
    featuresQuota: {
      cooperatorLimit: Number.POSITIVE_INFINITY,
      subAccount: 100,
    },
    monthlyResetQuota: {
      entries: 1000000,
    },
    isListedPublicly: true,
    supportMonthlyByDefault: false,
  },
]

export function getPlanByCode(planCode?: Maybe<string>) {
  return planCode ? PLANS.find(({ code }) => code === planCode) : undefined
}

export function isPlanCodeGtOther(one?: PlanCode, other?: PlanCode) {
  return (getPlanByCode(one)?.weight ?? 0) > (getPlanByCode(other)?.weight ?? 0)
}
